import * as React from "react"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./select"
import { cn } from "@/lib/utils"

/**
 * Generate time slots for the picker
 * @param {number} intervalMinutes - Interval between time slots (default: 30)
 * @param {string} startTime - Start time in HH:MM format (default: "06:00")
 * @param {string} endTime - End time in HH:MM format (default: "21:00")
 */
function generateTimeSlots(intervalMinutes = 30, startTime = "06:00", endTime = "21:00") {
  const slots = []
  const [startHour, startMin] = startTime.split(":").map(Number)
  const [endHour, endMin] = endTime.split(":").map(Number)
  
  let currentHour = startHour
  let currentMin = startMin
  
  while (currentHour < endHour || (currentHour === endHour && currentMin <= endMin)) {
    const hour24 = currentHour
    const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24
    const period = hour24 >= 12 ? "PM" : "AM"
    const minuteStr = currentMin.toString().padStart(2, "0")
    const value = `${hour24.toString().padStart(2, "0")}:${minuteStr}`
    const label = `${hour12}:${minuteStr} ${period}`
    
    slots.push({ value, label })
    
    currentMin += intervalMinutes
    if (currentMin >= 60) {
      currentHour += Math.floor(currentMin / 60)
      currentMin = currentMin % 60
    }
  }
  
  return slots
}

const TIME_SLOTS = generateTimeSlots(15, "06:00", "21:00")

/**
 * TimePicker Component
 * A dropdown-based time picker with preset time slots
 */
const TimePicker = React.forwardRef(({ 
  value, 
  onChange, 
  placeholder = "Select time",
  className,
  disabled = false,
  ...props 
}, ref) => {
  // Convert 12-hour format to 24-hour if needed
  const normalizeValue = (val) => {
    if (!val) return ""
    // If already in HH:MM format, return as-is
    if (/^\d{2}:\d{2}$/.test(val)) return val
    // Try to parse 12-hour format
    const match = val.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
    if (match) {
      let hour = parseInt(match[1], 10)
      const min = match[2]
      const period = match[3].toUpperCase()
      if (period === "PM" && hour !== 12) hour += 12
      if (period === "AM" && hour === 12) hour = 0
      return `${hour.toString().padStart(2, "0")}:${min}`
    }
    return val
  }

  const normalizedValue = normalizeValue(value)

  return (
    <Select
      value={normalizedValue}
      onValueChange={onChange}
      disabled={disabled}
    >
      <SelectTrigger 
        ref={ref} 
        className={cn("w-full", className)}
        {...props}
      >
        <SelectValue placeholder={placeholder}>
          {normalizedValue ? TIME_SLOTS.find(s => s.value === normalizedValue)?.label || value : placeholder}
        </SelectValue>
      </SelectTrigger>
      <SelectContent 
        position="popper" 
        sideOffset={5}
        className="max-h-[300px] overflow-y-auto"
      >
        {TIME_SLOTS.map((slot) => (
          <SelectItem key={slot.value} value={slot.value}>
            {slot.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
})

TimePicker.displayName = "TimePicker"

/**
 * DateTimePicker Component
 * Combines a date input with the TimePicker dropdown
 * Returns value in ISO datetime format (YYYY-MM-DDTHH:MM)
 * Handles timezone-aware ISO strings by converting to local display
 */
const DateTimePicker = React.forwardRef(({ 
  value, 
  onChange, 
  datePlaceholder = "Select date",
  timePlaceholder = "Select time",
  className,
  disabled = false,
  ...props 
}, ref) => {
  // Parse the datetime value into date and time parts
  // Handle timezone-aware ISO strings properly
  const parseDateTime = (val) => {
    if (!val) return { date: "", time: "" }
    
    // If the value contains timezone info (like -08:00 or Z), parse it as a Date
    // and extract local date/time components
    if (val.includes("T") && (val.includes("-", 10) || val.includes("+", 10) || val.endsWith("Z"))) {
      try {
        const dateObj = new Date(val)
        if (!isNaN(dateObj.getTime())) {
          // Format date as YYYY-MM-DD in Pacific time
          const options = { timeZone: 'America/Los_Angeles', year: 'numeric', month: '2-digit', day: '2-digit' }
          const dateParts = dateObj.toLocaleDateString('en-CA', options) // en-CA gives YYYY-MM-DD format
          
          // Format time as HH:MM in Pacific time
          const timeOptions = { timeZone: 'America/Los_Angeles', hour: '2-digit', minute: '2-digit', hour12: false }
          const timeParts = dateObj.toLocaleTimeString('en-US', timeOptions).substring(0, 5)
          
          return { date: dateParts, time: timeParts }
        }
      } catch (e) {
        console.error("Error parsing datetime:", e)
      }
    }
    
    // Handle simple ISO format without timezone: YYYY-MM-DDTHH:MM
    if (val.includes("T")) {
      const [date, timePart] = val.split("T")
      return { date, time: timePart?.substring(0, 5) || "" }
    }
    
    // Handle space-separated format
    if (val.includes(" ")) {
      const [date, time] = val.split(" ")
      return { date, time: time?.substring(0, 5) || "" }
    }
    
    return { date: val, time: "" }
  }

  const { date, time } = parseDateTime(value)

  // Get the correct Pacific timezone offset for a given date (handles PST vs PDT)
  const getPacificOffset = (dateStr) => {
    try {
      const tempDate = new Date(dateStr + "T12:00:00Z")
      if (isNaN(tempDate.getTime())) return "-08:00"
      const utcHour = tempDate.getUTCHours()
      const pacificHour = parseInt(tempDate.toLocaleString("en-US", {
        timeZone: "America/Los_Angeles",
        hour: "2-digit",
        hour12: false
      }))
      let diff = pacificHour - utcHour
      if (diff > 12) diff -= 24
      if (diff < -12) diff += 24
      const sign = diff >= 0 ? "+" : "-"
      return `${sign}${String(Math.abs(diff)).padStart(2, "0")}:00`
    } catch {
      return "-08:00"
    }
  }

  const handleDateChange = (e) => {
    const newDate = e.target.value
    if (newDate && time) {
      const offset = getPacificOffset(newDate)
      const isoString = `${newDate}T${time}:00${offset}`
      onChange?.(isoString)
    } else {
      onChange?.(newDate || "")
    }
  }

  const handleTimeChange = (newTime) => {
    if (date && newTime) {
      const offset = getPacificOffset(date)
      const isoString = `${date}T${newTime}:00${offset}`
      onChange?.(isoString)
    } else if (newTime) {
      // No date selected yet — store just the time, use today's date for offset
      const today = new Date().toISOString().split("T")[0]
      const offset = getPacificOffset(today)
      const isoString = `${today}T${newTime}:00${offset}`
      onChange?.(isoString)
    } else {
      onChange?.(date || "")
    }
  }

  return (
    <div className={cn("flex gap-2", className)} {...props}>
      <input
        ref={ref}
        type="date"
        value={date}
        onChange={handleDateChange}
        disabled={disabled}
        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
      />
      <TimePicker
        value={time}
        onChange={handleTimeChange}
        placeholder={timePlaceholder}
        disabled={disabled}
        className="w-[140px] flex-shrink-0"
      />
    </div>
  )
})

DateTimePicker.displayName = "DateTimePicker"

export { TimePicker, DateTimePicker, generateTimeSlots }
